import React, { useState } from 'react'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Switch,
  Table,
  TableBody,
  TableContainer,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import type { CategoryGroup } from '@app/src/types/categoryPlan'
import OptionMappingRow from './OptionMappingRow'

type CategoryCardProps = {
  group: CategoryGroup
  categoryKey: string
  allCategoryKeys: { key: string; label: string }[]
  existingCategoryName: string | null
  defaultExpanded: boolean
  onToggle: () => void
  onRenameCategoryName: (newName: string) => void
  onRenameOption: (rawValue: string, newName: string) => void
  onMoveOption: (rawValue: string, targetCategoryKey: string) => void
  onRemoveOption: (rawValue: string) => void
  onRestoreOption: (rawValue: string) => void
  onSetReplaceMode: (replaceMode: boolean) => void
  onConvertToColumns: () => void
}

const CategoryCard: React.FC<CategoryCardProps> = ({
  group,
  categoryKey,
  allCategoryKeys,
  existingCategoryName,
  defaultExpanded,
  onToggle,
  onRenameCategoryName,
  onRenameOption,
  onMoveOption,
  onRemoveOption,
  onRestoreOption,
  onSetReplaceMode,
  onConvertToColumns,
}) => {
  const [isEditingName, setIsEditingName] = useState(false)
  const [editName, setEditName] = useState(group.suggestedCategoryName ?? '')
  const isNew = group.existingCategoryId == null
  const categoryName = group.suggestedCategoryName ?? existingCategoryName ?? `Category #${group.existingCategoryId}`
  const activeOptions = group.optionMappings.filter(o => !o.isRemoved)
  const newOptionCount = activeOptions.filter(o => o.existingOptionId == null).length

  return (
    <Accordion
      elevation={0}
      disableGutters
      defaultExpanded={defaultExpanded}
      sx={{ border: 1, borderColor: 'divider', borderRadius: 2, '&:before': { display: 'none' } }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{ '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1 } }}
      >
        <Switch size="small" checked={group.isEnabled} onClick={e => e.stopPropagation()} onChange={onToggle} />
        {isNew && isEditingName ? (
          <TextField
            size="small"
            value={editName}
            onChange={e => setEditName(e.target.value)}
            onClick={e => e.stopPropagation()}
            onBlur={() => {
              setIsEditingName(false)
              if (editName.trim() && editName.trim() !== categoryName) {
                onRenameCategoryName(editName.trim())
              }
            }}
            onKeyDown={e => {
              if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
            }}
            autoFocus
            sx={{ minWidth: 160 }}
          />
        ) : (
          <Typography
            variant="subtitle2"
            onClick={
              isNew
                ? e => {
                    e.stopPropagation()
                    setIsEditingName(true)
                  }
                : undefined
            }
            sx={isNew ? { cursor: 'pointer', '&:hover': { textDecoration: 'underline' } } : {}}
          >
            {categoryName}
          </Typography>
        )}
        <Chip
          label={isNew ? 'new category' : 'existing'}
          size="small"
          variant="outlined"
          color={isNew ? 'warning' : 'default'}
        />
        <Chip
          label={`${(group.confidenceScore * 100).toFixed(0)}%`}
          size="small"
          color={group.confidenceScore >= 0.85 ? 'success' : group.confidenceScore >= 0.5 ? 'warning' : 'error'}
        />
        {!isNew && (
          <ToggleButtonGroup
            size="small"
            exclusive
            value={group.replaceMode ? 'replace' : 'add'}
            onChange={(e, value) => {
              e.stopPropagation()
              if (value === 'add' || value === 'replace') {
                onSetReplaceMode(value === 'replace')
              }
            }}
            onClick={e => e.stopPropagation()}
          >
            <ToggleButton value="add">Add</ToggleButton>
            <Tooltip title="Replaces the provider's existing options in this category. Other categories are unaffected.">
              <ToggleButton value="replace">Replace</ToggleButton>
            </Tooltip>
          </ToggleButtonGroup>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto', mr: 1 }}>
          {activeOptions.length} value{activeOptions.length !== 1 ? 's' : ''}
          {newOptionCount > 0 && ` (${newOptionCount} new)`}
        </Typography>
        <Tooltip title="Use this as a regular column instead, so you can map it to a field like Name.">
          <Button
            size="small"
            onClick={e => {
              e.stopPropagation()
              onConvertToColumns()
            }}
          >
            Use as column{group.sourceColumns.length > 1 ? 's' : ''}
          </Button>
        </Tooltip>
      </AccordionSummary>
      <AccordionDetails>
        <Box sx={{ mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Source columns:{' '}
            {group.sourceColumns.map(col => (
              <Chip key={col} label={col} size="small" variant="outlined" sx={{ mr: 0.5, mb: 0.5 }} />
            ))}
          </Typography>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableBody>
              {group.optionMappings.map(opt => (
                <OptionMappingRow
                  key={opt.rawValue}
                  option={opt}
                  categoryKeys={allCategoryKeys}
                  currentCategoryKey={categoryKey}
                  onRename={onRenameOption}
                  onMove={onMoveOption}
                  onRemove={onRemoveOption}
                  onRestore={onRestoreOption}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </AccordionDetails>
    </Accordion>
  )
}

export default CategoryCard
